import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { ArrowLeft, Megaphone } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { paths } from "@shared-core/routing/routes";
import { campaignTicketLabel } from "@shared-core/domain/campaign-ticket-label";
import type {
  MarketingPersonSummaryResponse,
  PersonCampaignHistoryLine,
} from "@shared-core/data/api-types";
import { formatTrail } from "@shared-core/domain/direct-mail-campaigns";
import { formatShortDateOrDash } from "@shared-core/domain/formatters";
import { TicketActivityTimeline } from "../app/components/domain/tickets/TicketActivityTimeline";
import { Badge } from "../app/components/primitives/badge";
import { consentVariant } from "../app/components/domain/marketing/marketingDisplay";

const CONSENT_CHANNELS: { key: string; label: string }[] = [
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "direct_mail", label: "Direct mail" },
  { key: "enclosure", label: "Enclosure" },
];

const STATUS_LABEL: Record<string, string> = {
  open: "Open",
  in_progress: "In progress",
  waiting: "Waiting",
  resolved: "Resolved",
  closed: "Closed",
};

export function MarketingPersonDetailPage() {
  const { personId } = useParams<{ personId: string }>();
  const dataAdapter = useDataAdapter();
  const [summary, setSummary] = useState<MarketingPersonSummaryResponse | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Campaigns the contact has a tier in (direct-mail campaigns), with their
  // tier and trail. Loaded on its own so a failure here leaves the rest of
  // the page.
  const [tieredHistory, setTieredHistory] = useState<
    PersonCampaignHistoryLine[] | null
  >(null);
  const [historyError, setHistoryError] = useState(false);

  useEffect(() => {
    if (!personId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    dataAdapter
      .getMarketingPersonSummary(personId)
      .then((data) => {
        if (!cancelled) setSummary(data);
      })
      .catch((e) => {
        console.error("marketing.contacts.summary.load_failed", e);
        if (!cancelled) setError("Couldn't load this contact.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, personId]);

  useEffect(() => {
    if (!personId) return;
    let cancelled = false;
    setTieredHistory(null);
    setHistoryError(false);
    dataAdapter
      .getPersonCampaignHistory(personId)
      .then((history) => {
        if (!cancelled) setTieredHistory(history);
      })
      .catch((e) => {
        console.error("marketing.contacts.campaign_history.load_failed", {
          person_id: personId,
          exc_type: e instanceof Error ? e.name : typeof e,
        });
        if (!cancelled) setHistoryError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, personId]);

  const tiered = tieredHistory ?? [];
  const campaigns = summary?.campaigns ?? [];
  const tickets = summary?.tickets ?? [];

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <Link
        to={paths.marketingPeople()}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        Back to contacts
      </Link>

      {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}

      {summary && (
        <>
          <div>
            <h1 className="text-2xl font-semibold">
              {summary.first_name} {summary.last_name}
            </h1>
            <div className="mt-1 text-sm text-muted-foreground space-y-0.5">
              {(summary.job_title || summary.company_name) && (
                <p>
                  {[summary.job_title, summary.company_name]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              )}
              {summary.email && <p>{summary.email}</p>}
              {summary.phone && <p>{summary.phone}</p>}
              {summary.postal_address && <p>{summary.postal_address}</p>}
              {summary.address_returned_at && (
                <p className="text-amber-800 dark:text-amber-300">
                  Address returned (
                  {[
                    summary.address_returned_context,
                    formatShortDateOrDash(summary.address_returned_at),
                  ]
                    .filter(Boolean)
                    .join(", ")}
                  ) – mailings leave them out until it is edited.
                </p>
              )}
              {summary.sales_rep_name && (
                <p>Sales rep: {summary.sales_rep_name}</p>
              )}
              {summary.customer_person_id != null && (
                <p>
                  Also a customer contact:{" "}
                  <Link
                    to={paths.contactDetail({
                      id: summary.customer_person_id,
                    })}
                    className="text-primary hover:underline"
                  >
                    this person
                  </Link>
                  .
                </p>
              )}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {CONSENT_CHANNELS.map((channel) => {
                const state = summary.consent?.[channel.key];
                if (state !== "allowed" && state !== "suppressed") return null;
                return (
                  <Badge key={channel.key} variant={consentVariant(state)}>
                    {channel.label}: {state}
                  </Badge>
                );
              })}
            </div>
          </div>

          <section>
            <h2 className="text-lg font-medium mb-2">
              Campaigns ({tiered.length + campaigns.length})
            </h2>
            {historyError && (
              <p className="text-sm text-red-600 mb-3">
                Couldn't load this contact's campaign tiers and trail.
              </p>
            )}
            {tieredHistory === null && !historyError && (
              <p className="text-sm text-muted-foreground mb-3">
                Loading campaign history…
              </p>
            )}
            {tiered.length > 0 && (
              <div className="border border-border rounded-md divide-y divide-border mb-3">
                {tiered.map((line) => (
                  <div key={line.campaign_id} className="px-3 py-2 text-sm">
                    <Link
                      to={paths.marketingCampaignWorkspace({
                        campaignId: line.campaign_id,
                      })}
                      className="font-medium text-foreground hover:text-primary hover:underline"
                    >
                      {line.campaign_name} · {line.tier_name}
                    </Link>
                    <p className="text-muted-foreground">
                      {formatTrail(line.trail)}
                    </p>
                  </div>
                ))}
              </div>
            )}
            {campaigns.length === 0 ? (
              tieredHistory !== null &&
              tiered.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  No campaigns sent to this contact yet.
                </p>
              )
            ) : (
              <div className="border border-border rounded-md divide-y divide-border">
                {campaigns.map((touch) => (
                  <div
                    key={touch.id}
                    className="px-3 py-2 flex items-center justify-between text-sm"
                  >
                    <span>{touch.campaign_name}</span>
                    <span className="text-muted-foreground">
                      {touch.channel} · {touch.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section>
            <h2 className="text-lg font-medium mb-2">
              Tickets ({tickets.length})
            </h2>
            {tickets.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No tickets raised for this contact yet.
              </p>
            ) : (
              <div className="space-y-4">
                {tickets.map((ticket) => {
                  const comments = ticket.comments ?? [];
                  const campaignName = campaignTicketLabel(ticket);
                  return (
                    <div
                      key={ticket.id}
                      className="border border-border rounded-md p-3"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="flex items-center gap-2 min-w-0">
                          <span className="font-medium text-sm truncate">
                            {ticket.title}
                          </span>
                          {campaignName && (
                            <Badge
                              variant="outline"
                              className="shrink-0 gap-1 border-pink-200 dark:border-pink-800 text-pink-700 dark:text-pink-300"
                              title={campaignName}
                            >
                              <Megaphone className="size-3 shrink-0" />
                              <span className="truncate max-w-[140px]">
                                {campaignName}
                              </span>
                            </Badge>
                          )}
                        </span>
                        <Badge variant="outline" className="shrink-0">
                          {STATUS_LABEL[ticket.status] ?? ticket.status}
                        </Badge>
                      </div>
                      {ticket.description && (
                        <p className="mt-1 text-sm text-muted-foreground">
                          {ticket.description}
                        </p>
                      )}
                      {comments.length > 0 && (
                        <div className="mt-3">
                          <TicketActivityTimeline
                            activity={comments}
                            resolveUserName={(userId) => userId}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
